"use client"

import { useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import { Button, Collapse, List, ListItemButton, ListItemText, Menu, MenuItem } from "@mui/material"
import { useLocale } from "next-intl"
import Link from "next/link"
import { usePathname } from "next/navigation"
import * as React from "react"
import { useState } from "react"
import { extractMenuProperties, getNonUmbracoUrl, mainMenuClick } from "utils"
import NewText from "../NewText/NewText"
import Text from "../Text/Text"
import { Desktop, Mobile, useIsDesktop } from "../common/bit/MediaQueries"
import { MenuArrowDown, MenuArrowRight, MenuArrowUp, NewWindowIconEn } from "../icons"
import NewWindowIcon from "../icons/NewWindowIcon"
import SubNavLinks from "./SubNavLinks"

export type NavLinkVariant = "default" | "port-mobile"

const StyledLink = styled("div", {
  shouldForwardProp: (prop) => !["selected", "color", "isLast", "variant"].includes(prop),
})<{
  selected?: boolean
  color: string
  isLast?: boolean
  variant?: NavLinkVariant
}>`
  position: relative;
  font-size: 16px;
  width: 100%;
  line-height: 24px;
  white-space: nowrap;
  color: ${(props) =>
    props.selected ? props.theme.colors.selectedMobileNavTextColor : props.color};

  text-decoration: none;
  p {
    font-weight: ${(props) => (props.selected ? "600" : "300")};
    text-decoration: none;
  }
  padding: 16px;
  border-bottom: ${(props) => (props.isLast ? "none" : " 1px solid #F7F7F7")};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0;
    display: flex;
    align-items: center;
    transition: 300ms all ease;
    text-decoration: none;
    height: 100%;
    border-bottom: none;

    p {
    }
    :hover {
      p {
        font-weight: 700;
        color: ${(props) => props.theme.colors.navTextColor};
      }
      text-decoration: underline;
    }
  }
  ${(props) =>
    props.variant === "port-mobile" &&
    `
      padding: 12px 0;
      border-bottom: none;
      color: ${props.theme.colors.port?.mobileMenuText || props.color};
      p {
        color: ${props.theme.colors.port?.mobileMenuText || props.color};
        font-weight: ${props.selected ? 600 : 300};
      }
    `}
`

const StyledMenuItem = styled("div", {
  shouldForwardProp: (prop) => !["active", "variant"].includes(prop),
})<{ active?: boolean; variant?: NavLinkVariant }>`
  a {
    text-decoration: none;
    color: #000;
    display: block;
  }
  white-space: nowrap;
  p {
    color: inherit;
    font-weight: ${(props) => (props.active ? 700 : "normal")};
  }
  padding: 13px 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0;
    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 16px;
      gap: 6px;

      color: inherit;
      font-weight: ${(props) => (props.active ? 700 : "300")};
      :hover {
        color: inherit;
        font-weight: 700;
      }
    }
  }
  ${(props) =>
    props.variant === "port-mobile" &&
    `
      padding: 8px 0;
      border-bottom: none;
      p {
        color: ${props.theme.colors.port?.mobileMenuText || props.theme.colors.black};
        font-weight: ${props.active ? 600 : 400};
      }
    `}
`

const StyledDiv = styled("div", {
  shouldForwardProp: (prop) => !["selected", "color", "isLast"].includes(prop),
})<{
  selected?: boolean
  color: string
  isLast?: boolean
}>`
  white-space: nowrap;

  color: ${(props) => props.color};
  position: relative;
  font-size: 18px;
  line-height: 24px;
  display: flex;
  align-items: center;
  text-decoration: none;
  p {
    font-weight: ${(props) => (props.selected ? "700" : "400")};
    text-decoration: none;
  }
  path {
    fill: ${(props) => props.color};
  }
  button {
    text-decoration: none;
    padding: 0;
    :hover {
      background: transparent;
      text-decoration: none;
    }
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    transition: 300ms all ease;
    border-bottom: none;
    :hover {
      p {
        text-decoration: underline;
        font-weight: 700;
      }
    }
  }
`

const StyledHash = styled("div", {
  shouldForwardProp: (prop) => !["selected", "color", "isLast", "variant"].includes(prop),
})<{
  selected?: boolean
  color: string
  isLast?: boolean
  variant?: NavLinkVariant
}>`
  font-size: 18px;
  line-height: 24px;
  color: ${(props) => props.color};

  text-decoration: none;
  padding: 16px;
  p {
    font-weight: ${(props) => (props.selected ? "500" : "400")};
    font-size: 14px;
    line-height: 100%;
  }
  border-bottom: ${(props) => (props.isLast ? "none" : " 1px solid #F7F7F7")};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0px 24px;
    border-bottom: none;
    display: flex;
    align-items: center;
    height: 100%;
    p {
      font-size: 16px;
      line-height: 20px;
    }
  }
  ${(props) =>
    props.variant === "port-mobile" &&
    `
      padding: 12px 0;
      border-bottom: none;
      p {
        font-size: 16px;
        color: ${props.theme.colors.port?.mobileMenuText || props.color};
      }
    `}
`

const StyledMobileButton = styled(ListItemButton, {
  shouldForwardProp: (prop) =>
    !["selected", "color", "isLast", "isOpen", "variant"].includes(prop as string),
})<{
  selected?: boolean
  color: string
  isLast?: boolean
  isOpen?: boolean
  variant?: NavLinkVariant
}>`
  font-size: 18px;
  line-height: 24px;

  text-decoration: none;
  padding: 16px;

  p {
    font-weight: ${(props) => (props.selected ? "600" : "400")};
    color: ${(props) =>
      props.selected ? props.theme.colors.selectedMobileNavTextColor : "#000000"};
  }
  path {
    fill: ${(props) =>
      props.selected ? props.theme.colors.selectedMobileNavTextColor : "#000000"};
  }
  border-bottom: ${(props) => (props.isLast ? "none" : " 1px solid #F7F7F7")};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0px 24px;
    border-bottom: none;
  }
  &:focus-visible {
    border: 2px dashed white;
    outline-offset: 2px;
    outline: 3px dashed #10059f;
  }
  background-color: ${(props) =>
    props.variant === "port-mobile"
      ? "transparent"
      : props.isOpen
      ? "#F7F7F7"
      : "transparent"} !important;
  ${(props) =>
    props.variant === "port-mobile" &&
    `
      padding: 12px 0;
      border-bottom: none;
      gap: 12px;
      p {
        color: ${props.theme.colors.port?.mobileMenuText || props.color};
      }
      path {
        fill: ${props.theme.colors.port?.mobileMenuText || props.color};
      }
    `}
`

const StyledButton = styled(Button)`
  display: flex;
  gap: 6px;
  align-items: center;
  text-transform: unset;
  &:hover {
    color: inherit;
  }
`

const StyledTextIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 16px;
  gap: 14px;
  width: 100%;
  @media (min-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    :hover {
      background-color: rgba(0, 0, 0, 0.04);
      li {
        font-weight: 400;
        color: #2768a3;
      }
      svg {
        path {
          fill: #2768a3;
        }
      }
    }
    li {
      &:hover {
        background-color: transparent;
        font-weight: 400;
        color: #2768a3;
      }
    }
  }
`

type Props = {
  href: string
  selected: boolean
  title: string
  color?: string
  onClick?: () => void
  isLast?: boolean
  subCategories?: any[]
  isOpen?: boolean
  // eslint-disable-next-line no-unused-vars
  setOpen?: (open: boolean) => void
  setSubMenuLink?: any
  companyName?: string
  navlinkdropdownprops?: any
  variant?: NavLinkVariant
}

function PortNavLink({
  href,
  selected,
  title,
  color: defaultColor,
  onClick,
  isLast,
  subCategories,
  isOpen: isOpen,
  setOpen: setOpen,
  setSubMenuLink,
  navlinkdropdownprops,
  variant = "default",
}: Props) {
  const theme = useTheme()
  const color = defaultColor || theme.colors.black
  const isDesktop = useIsDesktop()
  const locale = useLocale()
  const arrowColor =
    variant === "port-mobile" ? theme.colors.port?.mobileMenuText || "#0E2846" : "#1C1B1F"
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [subSubAnchorEl, setSubSubAnchorEl] = useState(null)
  const handleSubSubClick = (event: any) => {
    setSubSubAnchorEl(event?.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
    setSubSubAnchorEl(null)
    setOpen?.(false)
  }

  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    mainMenuClick(title, "")

    setAnchorEl(event.currentTarget.parentElement)
    setOpen?.(!isOpen)
  }

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault()
      event.stopPropagation()
      const menuItems = (event.currentTarget as HTMLElement).querySelectorAll(
        "[role=\"menuitem\"]",
      ) as NodeListOf<HTMLElement>
      if (!menuItems.length) return
      const active = document.activeElement as HTMLElement
      const currentIndex = Array.from(menuItems).findIndex(
        (el) => el === active || el.contains(active),
      )
      if (event.shiftKey) {
        const prevIndex = currentIndex <= 0 ? menuItems.length - 1 : currentIndex - 1
        menuItems[prevIndex]?.focus()
      } else {
        const nextIndex = currentIndex >= menuItems.length - 1 ? 0 : currentIndex + 1
        menuItems[nextIndex]?.focus()
      }
    }
  }

  const handleMobileClick = () => {
    mainMenuClick(title, "")
    setOpen?.(!isOpen)
  }

  const isExternalLink = (url: string) => {
    if (typeof window === "undefined") return false
    const link = new URL(url, window.location.href) // Create a URL object
    return link.hostname !== window.location.hostname
  }

  const pathname = usePathname()
  if (href && href.includes("#") && (pathname === "/" || pathname === "/en"))
    return (
      <Link
        href={href}
        target={href?.includes(".") ? "_blank" : "_self"}
        style={{ textDecoration: "none" }}
      >
        <StyledHash onClick={onClick} color={color} isLast={isLast} variant={variant}>
          <Mobile>
            <Text variant="LargeText" color={color}>
              {title}
            </Text>
          </Mobile>
          <Desktop>
            <Text variant="MediumText" color={color}>
              {title}
            </Text>
          </Desktop>
        </StyledHash>
      </Link>
    )
  if (subCategories?.length) {
    return (
      <>
        <Mobile>
          <List
            sx={{
              width: "100%",
              padding: 0,
              bgcolor: "background.paper",
              ...(typeof navlinkdropdownprops !== "undefined" ? navlinkdropdownprops : {}),
            }}
            component="nav"
            aria-labelledby="nested-list-subheader"
          >
            <StyledMobileButton
              selected={selected}
              color={color}
              id="basic-button"
              isOpen={isOpen}
              variant={variant}
              aria-controls={isOpen ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={isOpen ? "true" : undefined}
              onClick={handleMobileClick}
            >
              <ListItemText
                primary={
                  <NewText
                    component="p"
                    desktopFontSize={16}
                    desktopLineHeight={"20px"}
                    mobileFontSize={variant === "port-mobile" ? 16 : 14}
                    mobileLineHeight={variant === "port-mobile" ? "24px" : "100%"}
                    mobileFontWeight={
                      variant === "port-mobile" ? (selected ? 600 : 400) : undefined
                    }
                    color={
                      variant === "port-mobile" ? theme.colors.port?.mobileMenuText || color : color
                    }
                  >
                    {title}
                  </NewText>
                }
              />
              {!isOpen ? <MenuArrowUp fill={arrowColor} /> : <MenuArrowDown fill={arrowColor} />}
            </StyledMobileButton>
            <Collapse
              in={isOpen}
              timeout="auto"
              unmountOnExit
              style={{
                backgroundColor: isOpen ? "#F7F7F7" : "transparent",
                paddingRight: "16px",
              }}
            >
              <List component="div" disablePadding>
                {subCategories.map((item, index) => {
                  const { title: titleName, url } = item
                    ? extractMenuProperties(item)
                    : { title: "", url: "/" }
                  if (!titleName) return null
                  const activeSubItem = decodeURI(pathname) + "/" === getNonUmbracoUrl(url)

                  const activeChildItem = item?.children?.some((el: any) => {
                    return (
                      decodeURI(pathname) + "/" === getNonUmbracoUrl(extractMenuProperties(el).url)
                    )
                  })
                  return (
                    <React.Fragment key={index.toString()}>
                      {item?.children?.length ? (
                        <SubNavLinks
                          closeMenu={onClick}
                          title={titleName}
                          data={item?.children}
                          pathname={pathname}
                          color={color}
                          variant={variant}
                        />
                      ) : (
                        <StyledMenuItem
                          key={index.toString()}
                          active={activeSubItem || activeChildItem}
                          variant={variant}
                        >
                          <Link
                            onClick={() => {
                              setOpen?.(false)
                              mainMenuClick(title, titleName)
                            }}
                            href={getNonUmbracoUrl(url)}
                            target={url?.includes(".") ? "_blank" : "_self"}
                          >
                            <ListItemButton
                              onClick={onClick}
                              sx={{ padding: 0, pl: variant === "port-mobile" ? 0 : 1.5 }}
                              tabIndex={0}
                            >
                              {isExternalLink(url) ? (
                                <StyledTextIconWrapper>
                                  <ListItemText
                                    primary={
                                      <NewText
                                        component="p"
                                        desktopFontSize={16}
                                        desktopLineHeight={"14px"}
                                        mobileFontSize={variant === "port-mobile" ? 16 : 14}
                                        color={
                                          variant === "port-mobile"
                                            ? theme.colors.port?.mobileMenuText || color
                                            : color
                                        }
                                      >
                                        {titleName}
                                      </NewText>
                                    }
                                  />
                                  {locale === "en" ? (
                                    <NewWindowIconEn width={20} height={20} />
                                  ) : (
                                    <NewWindowIcon width={20} height={20} />
                                  )}
                                </StyledTextIconWrapper>
                              ) : (
                                <ListItemText
                                  primary={
                                    <NewText
                                      component="p"
                                      desktopFontSize={16}
                                      desktopLineHeight={"14px"}
                                      mobileFontSize={variant === "port-mobile" ? 16 : 14}
                                      color={
                                        variant === "port-mobile"
                                          ? theme.colors.port?.mobileMenuText || color
                                          : color
                                      }
                                    >
                                      {titleName}
                                    </NewText>
                                  }
                                />
                              )}
                            </ListItemButton>
                          </Link>
                        </StyledMenuItem>
                      )}
                    </React.Fragment>
                  )
                })}
              </List>
            </Collapse>
          </List>
        </Mobile>
        <Desktop>
          <StyledDiv color={color} isLast={isLast} selected={open || selected}>
            <StyledButton
              id="basic-button"
              aria-controls={open ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
            >
              <Desktop>
                <NewText
                  component={"p"}
                  desktopFontSize={18}
                  desktopLineHeight={"27px"}
                  desktopFontWeight={300}
                  inheritColor
                >
                  {title}
                </NewText>
              </Desktop>

              {!isOpen ? <MenuArrowUp fill="#1C1B1F" /> : <MenuArrowDown fill="#1C1B1F" />}
            </StyledButton>
            {anchorEl && (
              <Menu
                disableScrollLock={true}
                id="basic-menu"
                anchorEl={anchorEl}
                open={!!isOpen && isDesktop}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
                slotProps={{
                  paper: {
                    onKeyDownCapture: handleMenuKeyDown,
                  },
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: locale === "he" ? "right" : "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: locale === "he" ? "right" : "left",
                }}
                sx={{
                  display: {
                    xs: "none",
                    md: "block",
                  },
                  ".MuiPaper-root": {
                    // Target the root paper element of the menu
                    borderRadius: " 0 0 8px 8px", // Set your desired border radius here
                    boxShadow: "0px 8px 16px 0px rgba(0, 0, 0, 0.08)",
                    top: "88px !important",
                    backgroundColor: theme.colors.navBackgroundColor,
                    ...(typeof navlinkdropdownprops !== "undefined" ? navlinkdropdownprops : {}),
                    // Add any other styling you need here
                  },
                  zIndex: 1000001,
                }}
              >
                {subCategories.map((item, index) => {
                  const { title: titleName, url } = extractMenuProperties(item)

                  if (!titleName) return null
                  const activeSubItem = decodeURI(pathname) + "/" === getNonUmbracoUrl(url)

                  const activeChildItem = item?.children?.some((el: any) => {
                    return (
                      decodeURI(pathname) + "/" === getNonUmbracoUrl(extractMenuProperties(el).url)
                    )
                  })
                  const openSubItem =
                    Boolean(subSubAnchorEl) &&
                    isDesktop &&
                    index ===
                      Number(
                        (subSubAnchorEl as unknown as HTMLDivElement)?.getAttribute("data-index"),
                      )

                  return item?.children?.length ? (
                    <React.Fragment key={index.toString()}>
                      <StyledMenuItem
                        key={index.toString()}
                        active={activeSubItem || activeChildItem || openSubItem}
                      >
                        <MenuItem disableRipple onClick={handleSubSubClick} data-index={index}>
                          {titleName}
                          {locale === "en" ? (
                            <MenuArrowRight style={{ transform: "rotate(180deg)" }} />
                          ) : (
                            <MenuArrowRight />
                          )}
                        </MenuItem>
                      </StyledMenuItem>
                      {item?.children?.length ? (
                        <Menu
                          anchorEl={subSubAnchorEl}
                          open={openSubItem}
                          onClose={handleClose}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: locale === "en" ? "right" : "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: locale === "en" ? "left" : "right",
                          }}
                          sx={{
                            ".MuiPaper-root": {
                              // Target the root paper element of the menu
                              borderRadius: "8px", // Set your desired border radius here
                              minWidth: "180px",
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                              boxShadow: "0px 8px 16px 0px rgba(0, 0, 0, 0.08)",
                              backgroundColor: theme.colors.navBackgroundColor,

                              ...(typeof navlinkdropdownprops !== "undefined"
                                ? navlinkdropdownprops
                                : {}),
                              // Add any other styling you need here
                            },
                          }}
                        >
                          {item?.children?.map((it: any, ind: any) => {
                            const { title, url } = extractMenuProperties(it)

                            const activeSubItem =
                              decodeURI(pathname) + "/" === getNonUmbracoUrl(url)
                            return (
                              <StyledMenuItem key={ind.toString()} active={activeSubItem}>
                                <Link
                                  href={getNonUmbracoUrl(url)}
                                  target={url?.includes(".") ? "_blank" : "_self"}
                                >
                                  <MenuItem
                                    disableRipple
                                    onClick={() => {
                                      handleClose()
                                    }}
                                  >
                                    {title}
                                  </MenuItem>
                                </Link>
                              </StyledMenuItem>
                            )
                          })}
                        </Menu>
                      ) : null}
                    </React.Fragment>
                  ) : (
                    <StyledMenuItem key={index.toString()} tabIndex={0} active={activeSubItem}>
                      <Link
                        target={url?.includes(".") ? "_blank" : "_self"}
                        href={getNonUmbracoUrl(url)}
                        onClick={() => {
                          handleClose()
                          mainMenuClick(title, titleName)
                          setSubMenuLink(title)
                        }}
                        color={color}
                      >
                        {isExternalLink(getNonUmbracoUrl(url)) ? (
                          <StyledTextIconWrapper>
                            <MenuItem disableRipple>{titleName}</MenuItem>
                            {locale === "en" ? (
                              <NewWindowIconEn width={22} height={22} />
                            ) : (
                              <NewWindowIcon width={22} height={22} />
                            )}
                          </StyledTextIconWrapper>
                        ) : (
                          <MenuItem disableRipple>{titleName}</MenuItem>
                        )}
                      </Link>
                    </StyledMenuItem>
                  )
                })}
              </Menu>
            )}
          </StyledDiv>
        </Desktop>
      </>
    )
  }

  return (
    <Link
      href={href || ""}
      target={href?.includes(".") ? "_blank" : "_self"}
      style={{
        textDecoration: "none",
      }}
    >
      <StyledLink
        onClick={onClick}
        selected={selected}
        color={color}
        isLast={isLast}
        variant={variant}
      >
        <NewText
          component="p"
          desktopFontSize={18}
          desktopLineHeight={"27px"}
          mobileFontSize={variant === "port-mobile" ? 16 : 14}
          mobileLineHeight={variant === "port-mobile" ? "24px" : "100%"}
          desktopFontWeight={selected ? 600 : 300}
          mobileFontWeight={variant === "port-mobile" ? (selected ? 600 : 300) : undefined}
          inheritColor
        >
          {title}
        </NewText>
      </StyledLink>
    </Link>
  )
}

export default PortNavLink
